import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { RealtimeEvent } from '../../services/api';
import { REALTIME_ALERT_LABELS } from '../../features/realtime/config';
import { formatRealtimeTime } from '../../features/realtime/presentation';
import { SegmentPreviewGallery, SegmentPreviewGalleryItem } from '../SegmentPreviewGallery';
import ResultCardFrame from '../ResultCardFrame';
import { DeferredPreviewTile, PreviewViewportProvider } from '../GrantedPreviewImage';
import { RealtimeThumbnail } from './RealtimeEventDetails';
import { MetadataValueView } from '../MetadataViewControls';
import './RealtimeEventGallery.css';

export const RealtimeAlertTag: React.FC<{ event: RealtimeEvent }> = ({ event }) => <span className={`realtime-alert-tag realtime-alert-${event.alert_level}`}>{REALTIME_ALERT_LABELS[event.alert_level]}</span>;
export const RealtimeLoading: React.FC<{ label: string }> = ({ label }) => <div className="realtime-loading" role="status"><Loader2 size={18} className="realtime-spin" aria-hidden="true" /><span>{label}</span></div>;

interface RealtimeEventCollectionProps {
  events: RealtimeEvent[];
  onSelect: (event: RealtimeEvent) => void;
  label: string;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
}

const RealtimeEventSummary: React.FC<{
  event: RealtimeEvent;
  detailed?: boolean;
  showDetails?: boolean;
  onSelect?: () => void;
}> = ({ event, detailed = false, showDetails = true, onSelect }) => <>
  <header><RealtimeAlertTag event={event} />{showDetails && typeof event.score === 'number' && <span>{Math.round(event.score * 100)}%</span>}</header>
  <h3>{onSelect ? <button type="button" className="realtime-event-list-title" aria-label={`Open ${event.title}`} onClick={onSelect}>{event.title}</button> : event.title}</h3>
  <p>{event.description}</p>
  {detailed && event.alert_reason && <p className="realtime-event-list-reason">{event.alert_reason}</p>}
  {detailed && event.tags.length > 0 && <ul className="realtime-event-list-tags" aria-label="Observation tags">{event.tags.map((tag, index) => <li key={`${index}-${tag}`}>{tag}</li>)}</ul>}
  {showDetails && <footer><time dateTime={event.captured_end_at}>{formatRealtimeTime(event.captured_end_at)}</time></footer>}
</>;

const RealtimeEventListCard: React.FC<{ event: RealtimeEvent; onSelect: (event: RealtimeEvent) => void }> = ({ event, onSelect }) => {
  const [expanded, setExpanded] = useState(false);
  const [collapsedPaths, setCollapsedPaths] = useState<Set<string>>(new Set());
  return <article className={`realtime-event-list-card realtime-alert-${event.alert_level}`} aria-label={event.title}>
    <button type="button" className="realtime-event-list-preview" aria-label={`Preview ${event.title}`} onClick={() => onSelect(event)}>
      <DeferredPreviewTile><RealtimeThumbnail event={event} /></DeferredPreviewTile>
    </button>
    <div className="realtime-event-list-body" role="group" aria-label="Observation content">
      <div className="realtime-event-list-content"><RealtimeEventSummary event={event} detailed showDetails={expanded} onSelect={() => onSelect(event)} />
        <button type="button" className="action-button realtime-event-list-toggle" aria-expanded={expanded} onClick={() => setExpanded(current => !current)}>
          {expanded ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}{expanded ? 'Collapse details' : 'Expand details'}
        </button>
      </div>
      {expanded && Object.keys(event.extracted_metadata).length > 0 && <section className="realtime-event-list-fields" aria-label="Extracted fields">
        <h4>Extracted fields</h4>
        <MetadataValueView mode="fields" value={event.extracted_metadata} collapsedPaths={collapsedPaths}
          onTogglePath={path => setCollapsedPaths(current => { const next = new Set(current); if (next.has(path)) next.delete(path); else next.add(path); return next; })}
          sortBy="alpha" sortDirection="asc" />
      </section>}
    </div>
  </article>;
};

/** Expandable observations for the feed's scrollable side column. */
export const RealtimeEventList: React.FC<RealtimeEventCollectionProps & { showHeading?: boolean }> = ({ events, onSelect, label, loading, error, onRetry, showHeading = true }) => (
  <section className="realtime-event-list">
    {showHeading && <header><h3>{label}</h3>{events.length > 0 && <span>{events.length} observation{events.length === 1 ? '' : 's'}</span>}</header>}
    <PreviewViewportProvider><ol className="realtime-event-list-items" aria-label={label}>
      {events.map(event => <li key={event.event_id}><RealtimeEventListCard event={event} onSelect={onSelect} /></li>)}
    </ol></PreviewViewportProvider>
    {loading && <RealtimeLoading label="Loading observations" />}
    {!events.length && !loading && !error && <p className="realtime-event-list-empty">No matching observations yet.</p>}
    {error && <div className="realtime-history-error" role="status"><span>Observations could not be loaded.</span>{onRetry && <button type="button" className="action-button" onClick={onRetry}>Try again</button>}</div>}
  </section>
);

export const RealtimeEventStrip: React.FC<RealtimeEventCollectionProps> = ({ events, onSelect, label, loading, error, onRetry }) => (
  <section className="realtime-observation-strip">
    <header><h3>{label}</h3><span>{events.length ? `${events.length} recent` : ''}</span></header>
    <SegmentPreviewGallery ariaLabel={label} emptyState={loading ? <RealtimeLoading label="Loading observations" /> : <span>No matching observations yet.</span>}>
      {events.map(event => <SegmentPreviewGalleryItem key={event.event_id} title={event.title} selected={false} status="successful" onSelect={() => onSelect(event)} timeLabel={formatRealtimeTime(event.captured_end_at)} className={`realtime-history-preview realtime-alert-${event.alert_level}`}>
        <RealtimeThumbnail event={event} /><span className="realtime-history-caption"><RealtimeAlertTag event={event} /><span>{event.title}</span></span>
      </SegmentPreviewGalleryItem>)}
    </SegmentPreviewGallery>
    {error && <div className="realtime-history-error" role="status"><span>Recent observations could not be loaded.</span><button type="button" className="action-button" onClick={onRetry}>Try again</button></div>}
  </section>
);

export const RealtimeEventGrid: React.FC<{ events: RealtimeEvent[]; onSelect: (event: RealtimeEvent) => void }> = ({ events, onSelect }) => (
  <PreviewViewportProvider><div className="realtime-event-grid">{events.map(event => <DeferredPreviewTile key={event.event_id}><ResultCardFrame label={`Open ${event.title}`} className={`realtime-event-card realtime-alert-${event.alert_level}`} onOpen={() => onSelect(event)}>
    <RealtimeThumbnail event={event} /><div className="realtime-card-content"><RealtimeEventSummary event={event} /></div>
  </ResultCardFrame></DeferredPreviewTile>)}</div></PreviewViewportProvider>
);
